import { DirectoryRevealChannel } from 'src/types/graphql'
import { deferToIdle } from 'src/utils/deferToIdle'
import ga from 'src/utils/pixels/gtag'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'

/**
 * Directory-listing analytics (plan §4.1). Every event here is
 * OBSERVED-ONLY — none belongs in `TIER_ROLLUP_MAP` and none may become a
 * Google Ads conversion. A directory row is unclaimed, unmonetized supply;
 * bidding on interaction with it would spend paid budget sending renters to
 * businesses SpotMe earns nothing from.
 *
 * `surface` is a required string-literal union everywhere a DirectoryCard can
 * mount, per the instrumentation rule — never defaulted at the call site.
 */
export type DirectorySurface =
  | 'search_floor'
  | 'search_map'
  | 'hub'
  | 'ficha_nearby'
  | 'root_hub'
  /** SSR'd cards on a ZERO-SUPPLY city page (2026-08-28) — above the fold, crawlable. */
  | 'search_zero_supply'
  | 'search_weak_supply'

/**
 * Fires once per (session, surface, location) when a directory card becomes
 * visible — the denominator for directory CTR. Deduped because the search
 * floor re-renders on every filter change and a hub scrolls the same cards
 * back into view.
 */
export function trackDirectoryImpression(args: {
  surface: DirectorySurface
  locationId: string
  position?: number
}): void {
  if (firedOncePerSession(`directory:${args.surface}:${args.locationId}`))
    return
  ga.event('directory_impression', {
    surface: args.surface,
    location_id: args.locationId,
    position: args.position
  })
}

/**
 * The MAP surface's exposure event (§4.2). Fires ONE session-deduped
 * `directory_impression` with `surface: 'search_map'` and the marker COUNT —
 * never one event per dot: a viewport can carry up to 150 subordinate
 * markers, and a dot is not a card, so per-facility CTR is meaningless here.
 * No `location_id`/`position` on this variant.
 *
 * Proxy caveat: this measures "directory markers were rendered on the map
 * this session", not "the user looked at the map" — on mobile the results
 * drawer can cover it. It is the denominator the map surface previously
 * lacked entirely (clicks existed with no impression), not a claim of
 * viewability.
 */
export function trackDirectoryMapImpression(count: number): void {
  if (count <= 0) return
  if (firedOncePerSession('directory:search_map')) return
  ga.event('directory_impression', { surface: 'search_map', count })
}

/** Click-through from a directory card to its ficha. Never deduped — each click is intent. */
export function trackDirectoryCardClick(args: {
  surface: DirectorySurface
  locationId: string
  position?: number
}): void {
  ga.event('directory_card_click', {
    surface: args.surface,
    location_id: args.locationId,
    position: args.position
  })
}

/**
 * A reveal resolved. `capped` routes to the distinct `directory_reveal_capped`
 * event because unmet demand per facility is what the claim pitch is built on
 * (plan §4.3) — it is a different fact from a successful reveal, not a
 * parameter value of one.
 *
 * The renter's contact NEVER goes into an event — only the channel.
 */
/** Where the reveal form was opened — the ficha's rail, or inline on a card in
 *  the zero-supply city guide. Required: the two surfaces answer different
 *  questions (does the guide shorten the path to a phone number?). */
export type DirectoryRevealSurface =
  | 'ficha'
  | 'search_zero_supply'
  | 'search_weak_supply'

export function trackDirectoryReveal(args: {
  locationId: string
  channel: DirectoryRevealChannel
  capped: boolean
  revealsRemaining?: number
  surface: DirectoryRevealSurface
}): void {
  if (args.capped) {
    ga.event('directory_reveal_capped', {
      location_id: args.locationId,
      surface: args.surface
    })
    return
  }
  ga.event('directory_reveal', {
    location_id: args.locationId,
    channel: args.channel,
    reveals_remaining: args.revealsRemaining,
    surface: args.surface
  })
}

/** Which revealed row a renter tapped — `whatsapp_try` is the phone-only guess. */
/** "Ver teléfono" on a city-guide card opened the inline reveal form. */
export function trackDirectoryPhoneOpen(args: {
  locationId: string
  position?: number
}): void {
  ga.event('directory_phone_open', {
    location_id: args.locationId,
    position: args.position
  })
}

export type DirectoryContactChannel =
  | 'whatsapp'
  | 'whatsapp_try'
  | 'phone'
  | 'email'
  | 'directions'

/**
 * A revealed contact row was tapped. Not deduped — the renter may try WhatsApp,
 * fall back to the phone, then open directions, and each is a distinct signal.
 * `whatsapp_try` vs `whatsapp` tells us how often the phone-only guess is
 * even attempted (2026-08-28). Observed-only, never a conversion.
 */
export function trackDirectoryContactClick(args: {
  locationId: string
  channel: DirectoryContactChannel
}): void {
  ga.event('directory_contact_click', {
    location_id: args.locationId,
    channel: args.channel
  })
}

export function trackDirectoryWebsiteClick(locationId: string): void {
  ga.event('directory_website_click', { location_id: locationId })
}

/** Which part of the ficha's "Dónde está" link was tapped. */
export type DirectoryMapPart = 'map' | 'label'

/**
 * The ficha's pin was opened in Google Maps (2026-09-26). `part: 'map'` is the
 * static map image, which 169 people tapped in 30 days while it was a dead
 * `<img>`; `label` is the "Abrir en Google Maps" line under it. Same href for
 * both — the ungated coordinate pin, never the business's own Google listing,
 * which carries the phone the reveal gates. Deferred to idle: the link opens a
 * new tab, so this page stays alive to send it. Observed-only.
 */
export function trackDirectoryMapOpen(args: {
  locationId: string
  part: DirectoryMapPart
}): void {
  deferToIdle(() =>
    ga.event('directory_map_open', {
      location_id: args.locationId,
      part: args.part
    })
  )
}

/**
 * Which ramp back to real SpotMe inventory the renter took from a directory
 * ficha. `verified_nearby` is the always-on card; `verified_nearby_capped` is
 * the same destination offered after the facility's monthly reveal cap refused
 * a contact — different intent, so they are not collapsed.
 * `verified_nearby_disclaimer` (2026-09-26) is the link under the disclaimer,
 * the only ramp above the fold on mobile — read after "no contract, no
 * protected payment, no coverage", so it is a third intent, kept apart too.
 */
export type DirectoryRecoveryTarget =
  | 'verified_nearby'
  | 'verified_nearby_capped'
  | 'verified_nearby_disclaimer'
  | 'whatsapp_capped'
  | 'whatsapp_no_supply'

/**
 * A renter left a directory ficha for verified SpotMe supply (2026-08-31).
 * This is the ONLY measurement of the renter-recovery half of the directory
 * bet — whether publishing unclaimed facilities feeds SpotMe or just feeds the
 * facilities. Until this shipped, that half was entirely unmeasured.
 *
 * The on-demand ramp on the same page is NOT duplicated here: it already fires
 * `storage_on_demand_card_click` with `surface: 'directory_ficha'` from
 * `OnDemandFichaCard`. Read both together for total recovery.
 *
 * Observed-only, like every `directory_*` event — never a rollup member and
 * never a Google Ads conversion.
 */
export function trackDirectoryRecoveryClick(args: {
  locationId: string
  target: DirectoryRecoveryTarget
}): void {
  ga.event('directory_recovery_click', {
    location_id: args.locationId,
    target: args.target
  })
}

export function trackDirectoryClaimStart(args: {
  locationId: string
  surface: 'ficha'
  /** `guest` = no account yet (the 2026-08-28 wall flip); `auth` = signed in. */
  mode: 'guest' | 'auth'
}): void {
  ga.event('directory_claim_start', {
    location_id: args.locationId,
    surface: args.surface,
    mode: args.mode
  })
}

/** A guest draft was adopted by the account that signed in — the wall was crossed. */
export function trackDirectoryClaimLinked(locationId: string): void {
  ga.event('directory_claim_linked', { location_id: locationId })
}

/** Which talk-to-us shortcut the owner took instead of the claim form. */
export type DirectoryHostContactChannel = 'whatsapp' | 'meeting'

/**
 * The claim section's low-friction host-lead shortcuts (2026-08-28): a
 * WhatsApp draft to the corporate line or the HubSpot meetings page. Both are
 * SUPPLY-side intent from a directory ficha; observed-only like every
 * `directory_*` event — never a rollup member, never a bid target.
 */
export function trackDirectoryHostContactClick(args: {
  locationId: string
  channel: DirectoryHostContactChannel
}): void {
  ga.event('directory_host_contact_click', {
    location_id: args.locationId,
    channel: args.channel
  })
}

/**
 * The claim reached a human (plan §3.1 → §4.1). Carries the evidence STRENGTH,
 * not the evidence: the useful question a month from now is "do stronger claims
 * convert into verified listings?", and answering it must not need the claim
 * table. Never a conversion — a claim is unmonetized supply-side intent, and
 * the whole `directory_*` family stays out of `TIER_ROLLUP_MAP`.
 */
export function trackDirectoryClaimSubmitted(args: {
  locationId: string
  strength: string
}): void {
  ga.event('directory_claim_submitted', {
    location_id: args.locationId,
    strength: args.strength
  })
}

/**
 * The claim panel actually showed demand evidence (§4.3, on-page pitch).
 * Session-deduped per facility — the panel can be toggled open repeatedly.
 * Carries WHICH signals rendered so "does demand evidence convert claims?"
 * can be answered per signal, without joining the reveal ledger. Observed-only
 * like the whole family.
 */
export function trackDirectoryClaimDemandShown(args: {
  locationId: string
  nearbyShown: boolean
  revealsShown: boolean
  cappedShown: boolean
}): void {
  if (firedOncePerSession(`directory:claim_demand:${args.locationId}`)) return
  ga.event('directory_claim_demand_shown', {
    location_id: args.locationId,
    nearby_shown: args.nearbyShown,
    reveals_shown: args.revealsShown,
    capped_shown: args.cappedShown
  })
}

/**
 * The facility asked for its own ficha to come down (plan §3.6). An
 * observed-only health signal: a removal rate that climbs is the directory
 * telling us the operators it publishes do not want to be there.
 */
export function trackDirectoryRemovalRequest(args: {
  locationId: string
  delivered: boolean
}): void {
  ga.event('directory_removal_request', {
    location_id: args.locationId,
    delivered: args.delivered
  })
}
